/**
 * OrderFormHeader Component
 *
 * Header with title, status badge, and action buttons for the order form.
 */

import { ShoppingCart, Save, Send } from 'lucide-react';
import { Badge, Button } from '@/components/ui';
import { PurchaseOrderStatus, PURCHASE_ORDER_STATUS_LABELS } from '@bstock/shared';

const ORDER_STATUS_VARIANTS: Record<PurchaseOrderStatus, 'warning' | 'success' | 'neutral' | 'primary'> = {
  [PurchaseOrderStatus.DRAFT]: 'warning',
  [PurchaseOrderStatus.SENT]: 'success',
  [PurchaseOrderStatus.PARTIALLY_RECEIVED]: 'primary',
  [PurchaseOrderStatus.RECEIVED]: 'success',
  [PurchaseOrderStatus.CANCELLED]: 'neutral',
};

interface OrderFormHeaderProps {
  orderNumber: string | null;
  supplierName: string | null;
  status: PurchaseOrderStatus;
  isSaving: boolean;
  isSending: boolean;
  isFormValid: boolean;
  hasSupplierId: boolean;
  onSave: () => void;
  onSendClick: () => void;
}

export function OrderFormHeader({
  orderNumber,
  supplierName,
  status,
  isSaving,
  isSending,
  isFormValid,
  hasSupplierId,
  onSave,
  onSendClick,
}: OrderFormHeaderProps) {
  const title = orderNumber ?? 'Nouvelle commande';

  return (
    <div className="flex items-center justify-between mb-6 shrink-0">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary-100 rounded-lg">
          <ShoppingCart className="w-5 h-5 text-primary-600" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-dark-500">{title}</h2>
            <Badge variant={ORDER_STATUS_VARIANTS[status]}>
              {PURCHASE_ORDER_STATUS_LABELS[status]}
            </Badge>
          </div>
          {supplierName && <p className="text-sm text-neutral-500">{supplierName}</p>}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <Button
          variant="secondary"
          onClick={onSave}
          disabled={isSaving || !hasSupplierId}
        >
          {isSaving ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin">...</span>
              Enregistrement...
            </span>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Enregistrer brouillon
            </>
          )}
        </Button>
        <Button
          variant="primary"
          onClick={onSendClick}
          disabled={!isFormValid || isSaving || isSending}
        >
          {isSending ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin">...</span>
              Envoi...
            </span>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Envoyer commande
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
